import type { ReactNode } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { TNavbarDropdownElementItem } from '@shared/dream-components';
import { NavbarMenuDropdownItemV2 as NavbarMenuDropdownItemElement } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import type { RootLoaderData } from '~/root';

import { getClientSideNavigationHandler, updateNodeAttributes } from '../utils/getNodeActionAndVisibility';
import { useIsLinkActive } from '../utils/useIsLinkActive';

interface Props {
  node: TNavbarDropdownElementItem;
  children: ReactNode | ReactNode[];
  screen: 'desktop' | 'mobile';
}

const NavbarMenuDropdownItem = ({ node, children, screen }: Props) => {
  const { subscription } = usePageContext();
  const isPremiumUser = subscription?.tier === 'premium';
  const { isLoggedIn, site } = useRouteLoaderData<RootLoaderData>('root') || {};
  const isActive = useIsLinkActive(node.attrs?.href);

  const updatedNode = updateNodeAttributes(node, Boolean(isLoggedIn), isPremiumUser, subscription);
  const handleClientSideNavigation = getClientSideNavigationHandler(updatedNode, subscription);

  // Apply theme rules applicable to navbar_dropdown_item
  // if they are missing and weren't set in the editor
  const themeRules = site?.theme_rules?.navbar_dropdown_item || {};
  const modifiedAttrs: Record<string, any> = {};
  (Object.keys(themeRules) as Array<keyof typeof themeRules>).forEach((ruleKey) => {
    themeRules[ruleKey]?.forEach((attributeKey: string) => {
      if (updatedNode?.attrs && !updatedNode.attrs[attributeKey as keyof typeof updatedNode.attrs]) {
        modifiedAttrs[attributeKey] = site?.primary_theme[ruleKey];
      }
    });
  });

  return (
    <NavbarMenuDropdownItemElement
      element={
        {
          ...updatedNode,
          attrs: {
            ...updatedNode.attrs,
            ...modifiedAttrs,
          },
        } as TNavbarDropdownElementItem
      }
      screen={screen}
      onClick={handleClientSideNavigation}
      isActive={isActive}
    >
      {children}
    </NavbarMenuDropdownItemElement>
  );
};

export default NavbarMenuDropdownItem;
